import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getCsrfToken } from "../../services/authService";
import "../../styles/AdminQuotes.css";

export default function AdminQuotes() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState({});
  const [quoteEmails, setQuoteEmails] = useState({});
  const [confirmSend, setConfirmSend] = useState(null);
  const [form, setForm] = useState({
    factusolQuoteNumber: "",
    file: null,
  });
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadQuotes();
  }, []);

  const filteredQuotes = useMemo(() => {
    return quotes.filter((quote) =>
      `
        ${quote.quoteNumber ?? ""}
        ${quote.factusolOrderId ?? ""}
        ${quote.factusolDeliveryNoteId ?? ""}
        ${quote.factusolInvoiceId ?? ""}
        ${quote.customerName ?? ""}
        ${quote.customerEmail ?? ""}
        ${quote.items?.map((item) => `${item.factusolProductCode ?? ""} ${item.description ?? ""}`).join(" ") ?? ""}
      `
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [quotes, search]);

  const loadQuotes = async () => {
    const response = await fetch("/api/admin/quotes", {
      credentials: "include",
    });

    if (response.ok) {
      setQuotes(await response.json());
    }
  };

  const createQuote = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const formData = new FormData();
    formData.append("factusolQuoteNumber", form.factusolQuoteNumber);

    if (form.file) {
      formData.append("file", form.file);
    }

    const response = await fetch("/api/admin/quotes/import-factusol", {
      method: "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
      body: formData,
    });

    if (response.ok) {
      setForm({
        factusolQuoteNumber: "",
        file: null,
      });

      setMessage(t("adminQuotes.created"));
      await loadQuotes();
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/quotes/${token}`;
    await navigator.clipboard.writeText(url);
    setMessage(t("adminQuotes.linkCopied"));
  };

  const convertQuote = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/quotes/${id}/convert`, {
      method: "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      await loadQuotes();
      setMessage(t("adminQuotes.converted"));
    }
  };

  const createDeliveryNote = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/quotes/${id}/delivery-note`, {
      method: "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      await loadQuotes();
      setMessage(t("adminQuotes.deliveryNoteCreated"));
    }
  };

  const createInvoice = async (id) => {
    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/quotes/${id}/invoice`, {
      method: "POST",
      credentials: "include",
      headers: {
        [csrf.headerName]: csrf.token,
      },
    });

    if (response.ok) {
      await loadQuotes();
      setMessage(t("adminQuotes.invoiceCreated"));
    }
  };

  const uploadAttachment = async (quoteId, files) => {
    const csrf = await getCsrfToken();

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "TECHNICAL_SHEET");

      await fetch(`/api/admin/quotes/${quoteId}/attachments`, {
        method: "POST",
        credentials: "include",
        headers: {
          [csrf.headerName]: csrf.token,
        },
        body: formData,
      });
    }

    await loadQuotes();
      setMessage(t("adminQuotes.pdfUploaded"));
    };

  const sendQuoteEmail = async () => {
    if (!confirmSend) return;

    const csrf = await getCsrfToken();

    const response = await fetch(
      `/api/admin/quotes/${confirmSend.quote.id}/send-email`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [csrf.headerName]: csrf.token,
        },
        body: JSON.stringify({
          emails: confirmSend.emails,
          attachmentIds: confirmSend.attachmentIds,
          documentType: confirmSend.documentType,
        }),
      }
    );

    if (response.ok) {
      setMessage(t("adminQuotes.quoteEmailSent"));
      setConfirmSend(null);
    }
  };

  const toggleAttachment = (quote, attachmentId) => {
    setSelectedAttachments((prev) => {
      const current =
        prev[quote.id] ?? quote.attachments?.map((attachment) => attachment.id) ?? [];

      return {
        ...prev,
        [quote.id]: current.includes(attachmentId)
          ? current.filter((id) => id !== attachmentId)
          : [...current, attachmentId],
      };
    });
  };

  const deleteAttachment = async (quoteId, attachmentId) => {
    const csrf = await getCsrfToken();

    const response = await fetch(
      `/api/admin/quotes/${quoteId}/attachments/${attachmentId}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          [csrf.headerName]: csrf.token,
        },
      }
    );

    if (response.ok) {
      await loadQuotes();
      setMessage(t("adminQuotes.attachmentDeleted"));
    }
  };

  const getEmailsValue = (quote) => {
    return quoteEmails[quote.id] ?? quote.customerEmail ?? "";
  };

  const updateQuoteEmails = (quoteId, value) => {
    setQuoteEmails((prev) => ({
      ...prev,
      [quoteId]: value,
    }));
  };

  const parseEmails = (value) => {
    return value
      .split(/[;,]/)
      .map((email) => email.trim())
      .filter(Boolean);
  };

  const openSendConfirmation = (quote, documentType = "QUOTE") => {
    const emails = parseEmails(getEmailsValue(quote));

    if (emails.length === 0) {
      setMessage(t("adminQuotes.noEmails"));
      return;
    }

    const attachmentIds =
      selectedAttachments[quote.id] ?? quote.attachments?.map((a) => a.id) ?? [];

    setConfirmSend({
      quote,
      emails,
      attachmentIds,
      documentType,
    });
  };

  const closeSendConfirmation = () => {
    setConfirmSend(null);
  };

  return (
    <main className="admin-quotes-page">
      <section className="admin-quotes-container">
        <button
          className="admin-back-btn"
          type="button"
          onClick={() => navigate("/admin")}
        >
          ← {t("adminQuotes.backToDashboard")}
        </button>

        <h1>{t("adminQuotes.title")}</h1>
        <p className="admin-quotes-subtitle">{t("adminQuotes.subtitle")}</p>

        {message && <div className="admin-quotes-message">{message}</div>}

        <div className="admin-quotes-toolbar">
          <input
            type="text"
            placeholder={t("adminQuotes.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <form className="admin-quotes-form" onSubmit={createQuote}>
          <input
            type="text"
            placeholder={t("adminQuotes.factusolQuote")}
            value={form.factusolQuoteNumber}
            onChange={(e) =>
              setForm({
                ...form,
                factusolQuoteNumber: e.target.value,
              })
            }
            required
          />

          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
          />

          <button type="submit">{t("adminQuotes.import")}</button>
        </form>

        <div className="admin-quotes-list">
          {filteredQuotes.length === 0 ? (
            <div className="admin-quotes-empty">{t("adminQuotes.empty")}</div>
          ) : (
            filteredQuotes.map((quote) => (
              <article key={quote.id} className="admin-quote-card">
                <div className="admin-quote-main">
                  <div>
                    <h3>{quote.quoteNumber}</h3>
                    <p>{quote.customerName}</p>
                    <div className="admin-quote-email-box">
                      <label>{t("adminQuotes.emails")}</label>

                      <textarea
                        value={getEmailsValue(quote)}
                        onChange={(e) => updateQuoteEmails(quote.id, e.target.value)}
                        placeholder={t("adminQuotes.emailsPlaceholder")}
                        rows={2}
                      />
                    </div>

                    {quote.pdfFileUrl && (
                      <a
                        href={quote.pdfFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="admin-quote-pdf"
                      >
                        {t("adminQuotes.viewPdf")}
                      </a>
                    )}

                    {quote.attachments?.length > 0 && (
                      <div className="admin-quote-attachments">
                        <h4>{t("adminQuotes.attachments")}</h4>

                        {quote.attachments.map((attachment) => (
                          <div key={attachment.id} className="admin-quote-attachment">
                            <label>
                              <input
                                type="checkbox"
                                checked={
                                  selectedAttachments[quote.id]?.includes(attachment.id) ??
                                  true
                                }
                                onChange={() => toggleAttachment(quote, attachment.id)}
                              />

                              <a
                                href={attachment.fileUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {attachment.fileName}
                              </a>
                            </label>

                            <button
                              type="button"
                              className="delete-attachment-btn"
                              onClick={() => deleteAttachment(quote.id, attachment.id)}
                            >
                              {t("adminQuotes.deleteAttachment")}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="admin-quote-meta">
                    <span>{Number(quote.amount).toFixed(2)} €</span>
                    <strong className={`quote-status quote-status-${quote.status}`}>
                      {t(`adminQuotes.statuses.${quote.status}`)}
                    </strong>

                    <div className="factusol-docs">
                      {quote.factusolOrderId && (
                        <p>
                          <strong>{t("adminQuotes.factusolOrder")}:</strong>{" "}
                          {quote.factusolOrderId}
                        </p>
                      )}

                      {quote.factusolDeliveryNoteId && (
                        <p>
                          <strong>{t("adminQuotes.factusolDeliveryNote")}:</strong>{" "}
                          {quote.factusolDeliveryNoteId}
                        </p>
                      )}

                      {quote.factusolInvoiceId && (
                        <p>
                          <strong>{t("adminQuotes.factusolInvoice")}:</strong>{" "}
                          {quote.factusolInvoiceId}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="admin-quote-actions">
                    <button type="button" onClick={() => openSendConfirmation(quote, "QUOTE")}>
                      {t("adminQuotes.sendQuoteEmail")}
                    </button>

                    <label className="upload-pdf-btn">
                      {t("adminQuotes.uploadPdfs")}

                      <input
                        type="file"
                        accept="application/pdf"
                        multiple
                        hidden
                        onChange={(e) => {
                          uploadAttachment(quote.id, e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>

                    {quote.status === "ACCEPTED" && (
                      <button
                        type="button"
                        className="convert-btn"
                        onClick={() => convertQuote(quote.id)}
                      >
                        {t("adminQuotes.convert")}
                      </button>
                    )}

                    {/* PEDIDO */}

                    {quote.status === "ORDER_CREATED" && (
                      <>
                        <button
                          type="button"
                          className="delivery-note-btn"
                          onClick={() => createDeliveryNote(quote.id)}
                        >
                          {t("adminQuotes.createDeliveryNote")}
                        </button>

                        <button
                          type="button"
                          className="send-order-btn"
                          onClick={() => openSendConfirmation(quote, "ORDER")}
                        >
                          {t("adminQuotes.sendOrderEmail")}
                        </button>
                      </>
                    )}

                    {/* ALBARÁN */}

                    {quote.status === "DELIVERY_NOTE_CREATED" && (
                      <>
                        {!quote.factusolInvoiceId && (
                          <button
                            type="button"
                            className="invoice-btn"
                            onClick={() => createInvoice(quote.id)}
                          >
                            {t("adminQuotes.createInvoice")}
                          </button>
                        )}

                        <button
                          type="button"
                          className="send-delivery-btn"
                          onClick={() => openSendConfirmation(quote, "DELIVERY_NOTE")}
                        >
                          {t("adminQuotes.sendDeliveryNoteEmail")}
                        </button>
                      </>
                    )}

                    {/* FACTURA */}

                    {quote.status === "INVOICED" && (
                      <button
                        type="button"
                        className="send-invoice-btn"
                        onClick={() => openSendConfirmation(quote, "INVOICE")}
                      >
                        {t("adminQuotes.sendInvoiceEmail")}
                      </button>
                    )}
                  </div>
                </div>

                {quote.items?.length > 0 && (
                  <div className="admin-quote-items">
                    <h4>{t("adminQuotes.items")}</h4>

                    <table className="quote-items-table">
                      <thead>
                        <tr>
                          <th>{t("adminQuotes.productCode")}</th>
                          <th>{t("adminQuotes.description")}</th>
                          <th>{t("adminQuotes.quantity")}</th>
                          <th>{t("adminQuotes.unitPrice")}</th>
                          <th>{t("adminQuotes.total")}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {quote.items.map((item, index) => (
                          <tr key={`${quote.id}-${index}`}>
                            <td>{item.factusolProductCode}</td>

                            <td>{item.description}</td>

                            <td>{Number(item.quantity).toLocaleString("es-ES")}</td>

                            <td>{Number(item.unitPrice).toFixed(2)} €</td>

                            <td>
                              {(item.quantity * item.unitPrice).toFixed(2)} €
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </section>

      {confirmSend && (
        <div className="send-confirm-overlay">
          <div className="send-confirm-modal">
            <h2>{t("adminQuotes.confirmSendTitle")}</h2>

            <p>
              <strong>{t("adminQuotes.quote")}:</strong>{" "}
              {confirmSend.quote.quoteNumber}
            </p>

            <h3>{t("adminQuotes.recipients")}</h3>

            <ul>
              {confirmSend.emails.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>

            <h3>{t("adminQuotes.attachments")}</h3>

            <ul>
              {confirmSend.quote.pdfFileName && (
                <li>{confirmSend.quote.pdfFileName}</li>
              )}

              {confirmSend.quote.attachments
                ?.filter((attachment) =>
                  confirmSend.attachmentIds.includes(attachment.id)
                )
                .map((attachment) => (
                  <li key={attachment.id}>{attachment.fileName}</li>
                ))}
            </ul>

            <div className="send-confirm-actions">
              <button type="button" onClick={closeSendConfirmation}>
                {t("adminQuotes.cancel")}
              </button>

              <button
                type="button"
                className="send-confirm-btn"
                onClick={sendQuoteEmail}
              >
                {t("adminQuotes.confirmSend")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}